import { createContext } from 'react';
import type { VMSSite, VMSCamera, HealthStatus, DashboardData, IncidentSeverity } from '../types/canvision.types';
import type {
  UseCANVMSSocketReturn,
  VMSEventPayload,
  VMSIncidentCreatedPayload,
  VMSStatusChangePayload,
  VMSEventUpdatedPayload,
  VMSIncidentUpdatedPayload,
} from 'containers/pages/CANvision/hooks/useCANVMSSocket';

// ---------------------------------------------------------------------------
// Claim Types
// ---------------------------------------------------------------------------

/** Info stored per claimed event in the claimedEvents map. */
export interface ClaimedEventInfo {
  operatorName: string;
  claimedAt: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Shape of the shared CANvision context value.
 *
 * This context provides site / camera data, WebSocket connection status,
 * system health, dashboard statistics, and UI coordination state that is
 * consumed by both the Dashboard and Video Wall routes.
 */
export interface CANvisionContextValue {
  // -- Sites ------------------------------------------------------------------
  sites: VMSSite[];
  sitesLoading: boolean;
  sitesError: string | null;
  refetchSites: () => void;

  // -- Command Center Selection -----------------------------------------------
  selectedCommandCenterId: number | null;
  setSelectedCommandCenterId: (id: number | null) => void;

  // -- Site Selection ---------------------------------------------------------
  selectedSiteId: number | null;
  setSelectedSiteId: (id: number | null) => void;

  // -- Cameras (for selected site) --------------------------------------------
  cameras: VMSCamera[];
  camerasLoading: boolean;
  camerasError: string | null;
  refetchCameras: () => void;

  // -- WebSocket --------------------------------------------------------------
  socketConnection: UseCANVMSSocketReturn;

  // -- WebSocket Event Payloads (last received, for consumer subscriptions) ---
  /** Last `canvms:event` payload received over the socket. */
  lastEvent: VMSEventPayload | null;
  /** Last `canvms:incident-created` payload received over the socket. */
  lastIncident: VMSIncidentCreatedPayload | null;
  /** Last `canvms:status` payload received over the socket. */
  lastStatusChange: VMSStatusChangePayload | null;
  /** Last `canvms:event-updated` payload received over the socket. */
  lastEventUpdate: VMSEventUpdatedPayload | null;
  /** Last `canvms:incident-updated` payload received over the socket. */
  lastIncidentUpdate: VMSIncidentUpdatedPayload | null;

  // -- Event Claim/Release (hard lock) ----------------------------------------
  /** Map of eventId -> claim info for events currently claimed by any operator. */
  claimedEvents: Map<number, ClaimedEventInfo>;
  /** Claim an event for review (emits `event:claim` to server). */
  claimEvent: (eventId: number, operatorName: string) => void;
  /** Release a previously claimed event (emits `event:release` to server). */
  releaseEvent: (eventId: number) => void;

  // -- System Health (GET /health, polled every 30 s) -------------------------
  systemHealth: HealthStatus | null;
  systemHealthLoading: boolean;

  // -- Dashboard Stats (GET /dashboard, polled every 30 s) --------------------
  dashboardStats: DashboardData | null;
  dashboardStatsLoading: boolean;

  // -- Refetch triggers (exposed for consumer components) ---------------------
  /** Re-poll GET /dashboard immediately (used after socket events). */
  refetchDashboard: () => void;

  // -- Attention Feed UI coordination -----------------------------------------
  /** Camera ID highlighted when hovering an event row (glow effect) */
  highlightedCameraId: number | null;
  setHighlightedCameraId: (id: number | null) => void;

  /** Camera ID to filter event/incident tables by (set via attention feed click) */
  filterByCameraId: number | null;
  setFilterByCameraId: (id: number | null) => void;

  // -- Sound Manager -----------------------------------------------------------
  /** Play the notification tone for the given severity. No-op when muted. */
  playIncidentSound: (severity: IncidentSeverity) => void;
  /** Whether incident sounds are currently muted. */
  isSoundMuted: boolean;
  /** Toggle mute state. Persists choice to localStorage. */
  toggleSoundMute: () => void;

  // -- Shared Drawer States ---------------------------------------------------
  siteManagerOpen: boolean;
  setSiteManagerOpen: (open: boolean) => void;
  cameraSettingsOpen: boolean;
  setCameraSettingsOpen: (open: boolean) => void;
  cameraSetManagerOpen: boolean;
  setCameraSetManagerOpen: (open: boolean) => void;
  checklistMappingOpen: boolean;
  setChecklistMappingOpen: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React Context for CANvision shared state.
 *
 * Initialised as `undefined` so that the consumer hook can detect when it is
 * used outside of `<CANvisionProvider>`.
 */
export const CANvisionContext = createContext<CANvisionContextValue | undefined>(
  undefined,
);
